import type { HTMLAttributes, ReactNode } from 'react';

import styles from './Card.module.scss';

type CardPadding = 'none' | 'sm' | 'md' | 'lg';

type CardProps = HTMLAttributes<HTMLDivElement> & {
  readonly padding?: CardPadding;
  readonly interactive?: boolean;
};

const paddingStyleMap: Record<CardPadding, string> = {
  none: styles.padNone,
  sm: styles.padSm,
  md: styles.padMd,
  lg: styles.padLg,
};

export function Card({
  padding = 'md',
  interactive = false,
  className,
  children,
  ...rest
}: CardProps) {
  const classes = [
    styles.card,
    paddingStyleMap[padding],
    interactive ? styles.interactive : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}

type CardHeaderProps = {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly actions?: ReactNode;
};

export function CardHeader({ title, subtitle, actions }: CardHeaderProps) {
  return (
    <div className={styles.header}>
      <div>
        <h3 className={styles.headerTitle}>{title}</h3>
        {subtitle ? <p className={styles.headerSubtitle}>{subtitle}</p> : null}
      </div>
      {actions ? <div>{actions}</div> : null}
    </div>
  );
}

export function CardBody({ children, className }: { readonly children: ReactNode; readonly className?: string }) {
  return <div className={[styles.body, className ?? ''].join(' ')}>{children}</div>;
}

export function CardFooter({ children }: { readonly children: ReactNode }) {
  return <div className={styles.footer}>{children}</div>;
}
